import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    Guideline_Role_Enum,
    Guideline_Status_Enum,
    Guideline_Type_Enum,
    GuidelineFragment,
    Guidelines_Insert_Input,
    UpdateGuidelineMutationVariables
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    SuperAdminVenueBookingGuidelineCreateFragment, 
    SuperAdminVenueBookingGuidelineCreateSchema, 
    SuperAdminVenueBookingGuidelineUpdateFragment, 
    SuperAdminVenueBookingGuidelineUpdateSchema 
} from "src/components/superadmin/venues/bookings/guidelines/SuperAdminVenueBookingGuidelineSchemas"
import { extractCurrentItemData } from "src/utils/forms"

type FormPayloadType = SuperAdminVenueBookingGuidelineCreateFragment

let initialState: FormPayloadType = {
    name: "",
    description: "",
    tenant_required: false,
    venue_required: false,
    talent_required: false,
    document_id: null,
    metadata: null,
};

export const CreateSuperAdminVenueBookingGuidelineDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().guidelines;
    const { loading, errors } = useAppState().guidelines;
    const { currentItemId: currentBookingId } = useAppState().bookings;
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Guidelines_Insert_Input) => {
        return createItem({ booking_id: currentBookingId, type: Guideline_Type_Enum.Booking, parent_guideline_type: Guideline_Type_Enum.Booking, ...payload })
    }

    return (
        <>
            <FormDrawer<FormPayloadType, GuidelineFragment, string>
                autoOpen={false}
                formSchema={SuperAdminVenueBookingGuidelineCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateSuperAdminVenueBookingGuidelineAction = () => {
    return (
        <PageDrawerAction<GuidelineFragment>
            title='Add Guideline'
            action={CreateSuperAdminVenueBookingGuidelineDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: SuperAdminVenueBookingGuidelineUpdateFragment = {
    status: Guideline_Status_Enum.Active,
    role: Guideline_Role_Enum.Info,
    name: "",
    description: "",
    tenant_required: false,
    venue_required: false,
    talent_required: false,
    document_id: null,
    metadata: null,
    tenant_confirmed: false,
    venue_confirmed: false,
    talent_confirmed: false,
    tenant_completed: false,
    venue_completed: false,
    talent_completed: false,    
    target_booking_type: null,
    target_booking_status: null,
};

export const UpdateSuperAdminVenueBookingGuidelineDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().guidelines;
    const { currentItem, loading, errors } = useAppState().guidelines;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: SuperAdminVenueBookingGuidelineUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<SuperAdminVenueBookingGuidelineUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateGuidelineMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<SuperAdminVenueBookingGuidelineUpdateFragment, GuidelineFragment, string>
                autoOpen={false}
                formSchema={SuperAdminVenueBookingGuidelineUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateSuperAdminVenueBookingGuidelineAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().guidelines;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.guidelines.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<GuidelineFragment>
            title='Edit Guideline'
            action={UpdateSuperAdminVenueBookingGuidelineDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditSuperAdminVenueBookingGuidelineAction = () => {
    return (
        <PageDrawerAction<GuidelineFragment>
            title='Edit Guideline'
            action={UpdateSuperAdminVenueBookingGuidelineDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}